import React, { useState, useEffect } from "react";
import { MdPayment } from "react-icons/md";
import DroitParticipationServiceInstance from "service/droitparticipation.service";
import HeaderSection from "components/headers-section";
import ActionBar from "components/action-bar";
import DroitParticipationTable from "./components/DroitParticipationTable";
import DroitParticipationFormModal from "./components/DroitParticipationFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";

const DroitParticipationManager = () => {
  const [droits, setDroits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentDroit, setCurrentDroit] = useState({
    name_droit: "",
    montant_droit: "",
    status_droit: true
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchDroits();
  }, []);

  const fetchDroits = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await DroitParticipationServiceInstance.getAll();
      setDroits(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les droits de participation. Veuillez réessayer plus tard."
      );
      console.error("Error fetching droits:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentDroit({
      name_droit: "",
      montant_droit: "",
      status_droit: true
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (droit) => {
    setCurrentDroit({
      id_droit: droit.id_droit,
      name_droit: droit.name_droit,
      montant_droit: droit.montant_droit,
      status_droit: droit.status_droit,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (droit) => {
    setCurrentDroit(droit);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await DroitParticipationServiceInstance.update(
          currentDroit.id_droit,
          formData
        );
      } else {
        await DroitParticipationServiceInstance.create(formData);
      }
      
      fetchDroits();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        errors: error.response?.data?.errors || {
          general: isEditing 
            ? "Impossible de modifier le droit de participation. Veuillez réessayer."
            : "Impossible de créer le droit de participation. Veuillez réessayer."
        }
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await DroitParticipationServiceInstance.delete(currentDroit.id_droit);
      fetchDroits();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer le droit de participation. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredDroits = droits.filter(
    (droit) => 
      droit.name_droit.toLowerCase().includes(searchTerm.toLowerCase()) ||
      droit.montant_droit.toString().includes(searchTerm)
  );

  return (
    <div className="px-5 py-8 md:px-8 bg-gray-50 min-h-screen">
        <HeaderSection
          title="Gestion des Droits de Participation"
          description="Créez et gérez les droits de participation et leurs montants"
          accentColor="#57A863"
        />

        <ActionBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Rechercher par nom ou montant..."
          searchIcon={MdPayment}
          onRefresh={fetchDroits}
          onCreateNew={openCreateModal}
          buttonLabel="Nouveau Droit"
          loading={loading}
          primaryColor="#57A863"
          hoverColor="#4c9657"
        />

        {error && (
          <div
            className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 border border-red-200"
            role="alert"
          >
            {error}
          </div>
        )}

        <DroitParticipationTable
          droits={filteredDroits}
          loading={loading}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

      <DroitParticipationFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialDroit={currentDroit}
        isEditing={isEditing}
        existingDroits={droits}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentDroit}
        itemName={currentDroit.name_droit}
        itemType="droit de participation"
        warning="La suppression de ce droit de participation peut affecter les opérations financières associées."
        loading={loading}
      />
    </div>
  );
};

export default DroitParticipationManager;